/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  ActivityIndicator,
  Image,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { showError, showSuccess, showInfo } from '../../shared/ui/appToast';
import { customerPortalBaseUrl } from '../../shared/customerPortalUrl';
import {
  getWorkOrderDetail,
  updateWorkOrderStatus,
  updateWorkOrderNotes,
  addWorkOrderTask,
  removeWorkOrderTask,
  addWorkOrderPart,
  removeWorkOrderPart,
  updateWorkOrderTask,
  updateWorkOrderPart,
  addWorkOrderChecklist,
  updateWorkOrderChecklistItems,
  completeWorkOrderChecklist,
  removeWorkOrderChecklist,
  listChecklistTemplates,
  deductWorkOrderPartStock,
  uploadWorkOrderPhoto,
  removeWorkOrderPhoto,
  linkWorkOrderToAppointment,
  type ChecklistTemplate,
} from '../infra/workOrdersRepo';
import {
  buildWorkOrderWhatsAppMessage,
  openWhatsAppWithMessage,
} from '../domain/workOrderWhatsApp';
import { formatOtLabel } from '../domain/otNumber';
import {
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUS_COLORS,
  nextStatuses,
  isClosedStatus,
} from '../domain/workOrderStatus';
import { SUBJECT_LABELS } from '../domain/workOrderSubject';
import type {
  WorkOrderDetail,
  WorkOrderStatus,
  WorkOrderChecklistItem,
  WorkOrderTask,
  WorkOrderPart,
} from '../domain/types';
import { searchProducts } from '../../catalog/infra-api'
import { pickProductImage, isProductImageValidationError } from '../../catalog/infra-api';
import type { ProductSearchRow } from '../../catalog/domain-api';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { useEmployeesStore, withSelfOwner } from '../../team/store/useEmployeesStore';
import { getVehicle } from '../../vehicles/infra/vehiclesRepo'
import { vehicleDisplay } from '../../vehicles/domain/types';
import { getCustomerAccountById } from '../../customers/public-api';
import { listBranchAppointments } from '../../appointments/infra/appointmentsRepo';
import type { Appointment } from '../../appointments/domain/types';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { formatMoney } from '../../shared/format/public-api';

function shortId(id: string): string {
  return id.slice(0, 8).toUpperCase();
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

/** Statuses that require confirmation before transitioning. */
const CONFIRM_STATUSES: WorkOrderStatus[] = ['cancelled', 'approved', 'in_progress'];

export function WorkOrderDetailScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const workOrderId: string = route.params?.workOrderId ?? '';
  const { currentBranchId, branches } = useSessionStore();
  const branchName = branches.find((b) => b.id === currentBranchId)?.name ?? null;
  const { employees: employeesRaw, selfOwner } = useEmployeesStore();
  const employees = withSelfOwner(employeesRaw, selfOwner);
  const canChangeStatus = useModulePermission('WorkOrders', 'change_status');
  const canManageParts = useModulePermission('WorkOrders', 'manage_parts');
  const canManagePhotos = useModulePermission('WorkOrders', 'manage_photos');
  const canManageChecklists = useModulePermission('WorkOrders', 'manage_checklists');
  const canLinkAppointment = useModulePermission('WorkOrders', 'link_appointment');
  const canEdit = useModulePermission('WorkOrders', 'edit');
  const canLinkSale = useModulePermission('WorkOrders', 'link_sale');
  const canAssignTasks = useModulePermission('WorkOrders', 'assign_tasks');
  const showMoney = canLinkSale;
  const capabilityGate = useCapabilityGate('work_orders', {
    title: 'Órdenes de trabajo no disponibles',
    subtitle: 'Este rubro no tiene órdenes de trabajo.',
  });

  const [detail, setDetail] = useState<WorkOrderDetail | null>(null);
  const [subjectLabel, setSubjectLabel] = useState<string | null>(null);
  const [customerPhone, setCustomerPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [linkAppointmentOpen, setLinkAppointmentOpen] = useState(false);
  const [appointmentOptions, setAppointmentOptions] = useState<Appointment[]>([]);
  const [linkingAppointment, setLinkingAppointment] = useState(false);

  // Add task/part modal state
  const [addModalMode, setAddModalMode] = useState<'task' | 'part' | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<ProductSearchRow[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductSearchRow | null>(null);
  const [itemQuantity, setItemQuantity] = useState('1');
  const [itemPrice, setItemPrice] = useState('');
  const [savingItem, setSavingItem] = useState(false);
  const [taskEmployeeId, setTaskEmployeeId] = useState<string | null>(null);
  const [taskCommission, setTaskCommission] = useState('');

  // Edit item modal state
  const [editModal, setEditModal] = useState<{ type: 'task' | 'part'; id: string; name: string; qty: number; price: number; employeeId?: string | null; commission?: number | null } | null>(null);
  const [editQty, setEditQty] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editEmployeeId, setEditEmployeeId] = useState<string | null>(null);
  const [editCommission, setEditCommission] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Edit notes modal state
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Photo viewer modal
  const [photoViewerUrl, setPhotoViewerUrl] = useState<string | null>(null);
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [depositAmountDraft, setDepositAmountDraft] = useState('');
  const [qrToken, setQrToken] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  const load = useCallback(async () => {
    if (!workOrderId) {
      setError('Falta el ID de la orden de trabajo.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const d = await getWorkOrderDetail(workOrderId);
      if (!d) {
        setError('No se encontró la orden de trabajo.');
        setDetail(null);
        setSubjectLabel(null);
        setCustomerPhone(null);
      } else {
        setDetail(d);
        // Resolve vehicle plate + customer phone for display / WhatsApp
        let label: string | null = null;
        if (d.workOrder.subjectType === 'vehicle' && d.workOrder.subjectId) {
          try {
            const v = await getVehicle(d.workOrder.subjectId);
            if (v) label = vehicleDisplay(v);
          } catch {
            label = null;
          }
        }
        setSubjectLabel(label);
        let phone: string | null = null;
        if (d.workOrder.customerId) {
          try {
            const c = await getCustomerAccountById(d.workOrder.customerId);
            phone = c?.phone?.trim() || null;
          } catch {
            phone = null;
          }
        }
        setCustomerPhone(phone);
      }
      const t = await listChecklistTemplates();
      setTemplates(t);
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar la orden');
      setDetail(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [workOrderId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    void load();
  }, [load]);

  // Debounced product search
  useEffect(() => {
    if (!addModalMode) return;
    const query = productSearch.trim();
    if (query.length < 2) {
      setProductResults([]);
      return;
    }
    setProductSearching(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const results = await searchProducts({
            search: query,
            posItemType: addModalMode === 'task' ? 'service' : 'all',
            limit: 20,
          });
          setProductResults(results);
        } catch {
          setProductResults([]);
        } finally {
          setProductSearching(false);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, addModalMode]);

  const handleStatusChange = useCallback(
    async (newStatus: WorkOrderStatus) => {
      // Confirm for significant transitions
      if (CONFIRM_STATUSES.includes(newStatus)) {
        const label = WORK_ORDER_STATUS_LABELS[newStatus];
        const ok = await confirm({
          title: 'Confirmar cambio de estado',
          message:
            newStatus === 'cancelled'
              ? '¿Seguro que querés cancelar esta OT? Esta acción no se puede deshacer.'
              : `¿Confirmás pasar la OT a "${label}"?`,
          confirmLabel: 'Confirmar',
          destructive: newStatus === 'cancelled',
        });
        if (!ok) return;
      }
      setTransitioning(true);
      try {
        await updateWorkOrderStatus(workOrderId, newStatus);
        showSuccess('Estado actualizado', `OT pasada a ${WORK_ORDER_STATUS_LABELS[newStatus]}`);
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo cambiar el estado.' });
      } finally {
        setTransitioning(false);
      }
    },
    [workOrderId, load, confirm],
  );

  const handleCobrar = useCallback(
    (kind: 'full' | 'deposit' | 'balance', amountDue?: number) => {
      if (!detail) return;
      const { tasks, parts, workOrder } = detail;
      const tasksTotal = tasks.reduce((sum, t) => sum + t.unitPrice * t.quantity, 0);
      const partsTotal = parts.reduce((sum, p) => sum + p.unitPrice * p.quantity, 0);
      const grandTotal = tasksTotal + partsTotal;
      if (kind !== 'deposit' && tasks.length === 0 && parts.length === 0) {
        void confirm({
          title: 'OT sin ítems',
          message: 'Agregá tareas o repuestos a la OT antes de cobrar.',
          confirmLabel: 'Entendido',
        });
        return;
      }
      const resolvedAmount =
        kind === 'full' ? grandTotal : amountDue;
      if (!resolvedAmount || resolvedAmount <= 0) {
        showError(kind === 'deposit' ? 'Seña' : 'Cobro', 'Ingresá un monto válido.');
        return;
      }
      const taskProductIds = tasks.map((t) => t.productId);
      const partProductIds = parts.map((p) => p.productId);
      const otLabel = formatOtLabel(workOrder.otNumber, workOrder.id);
      void (async () => {
        try {
          const { setPendingWorkOrderCheckout } = await import('../application/workOrderPosBridge');
          setPendingWorkOrderCheckout(workOrderId, taskProductIds, partProductIds, {
            kind,
            amountDue: resolvedAmount,
            otLabel,
          });
          setDepositModalOpen(false);
          navigateInShell(navigation, 'POS', { workOrderId });
        } catch (e: any) {
          presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo iniciar el cobro desde POS.' });
        }
      })();
    },
    [detail, workOrderId, navigation, confirm],
  );

  const handleShowQr = useCallback(async () => {
    if (!workOrderId) return;
    setQrLoading(true);
    try {
      const { ensureWorkOrderPublicToken } = await import('../infra/workOrdersRepo');
      const token = await ensureWorkOrderPublicToken(workOrderId);
      setQrToken(token);
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'work-orders', op: 'qr', error: e, title: 'QR', userMessage: 'No se pudo generar el QR.' });
    } finally {
      setQrLoading(false);
    }
  }, [workOrderId]);

  const handleWhatsApp = useCallback(async () => {
    if (!detail) return;
    try {
      const message = buildWorkOrderWhatsAppMessage(detail.workOrder, detail.tasks, detail.parts, branchName);
      await openWhatsAppWithMessage(customerPhone, message);
      showSuccess('WhatsApp', 'Abriendo WhatsApp…');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo abrir WhatsApp.' });
    }
  }, [detail, branchName, customerPhone]);

  const handleAddPhoto = useCallback(async () => {
    if (!detail || !canManagePhotos) return;
    try {
      const picked = await pickProductImage();
      if (!picked?.base64) return;
      setUploadingPhoto(true);
      await uploadWorkOrderPhoto({
        workOrderId,
        organizationId: detail.workOrder.organizationId,
        base64: picked.base64,
        mimeType: picked.mimeType || 'image/jpeg',
      });
      showSuccess('Foto agregada');
      await load();
    } catch (e: any) {
      if (isProductImageValidationError(e)) {
        showError('Imagen no válida', e.message);
      } else {
        presentUnexpectedFailure({ module: 'work-orders', op: 'fotos', error: e, title: 'Fotos', userMessage: 'No se pudo subir la foto.' });
      }
    } finally {
      setUploadingPhoto(false);
    }
  }, [detail, canManagePhotos, workOrderId, load]);

  const handleRemovePhoto = useCallback(
    async (photoId: string) => {
      if (!canManagePhotos) return;
      const ok = await confirm({
        title: 'Eliminar foto',
        message: '¿Sacar esta foto de la OT?',
        confirmLabel: 'Eliminar',
        destructive: true,
      });
      if (!ok) return;
      try {
        await removeWorkOrderPhoto(photoId);
        showSuccess('Foto eliminada');
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'fotos', error: e, title: 'Fotos', userMessage: 'No se pudo eliminar.' });
      }
    },
    [canManagePhotos, confirm, load],
  );

  const openLinkAppointment = useCallback(async () => {
    if (!currentBranchId || !canLinkAppointment) return;
    setLinkAppointmentOpen(true);
    try {
      const from = new Date();
      from.setDate(from.getDate() - 7);
      const to = new Date();
      to.setDate(to.getDate() + 30);
      const rows = await listBranchAppointments(
        currentBranchId,
        from.toISOString(),
        to.toISOString(),
        null,
      );
      setAppointmentOptions(
        rows.filter((a) => a.status === 'confirmed' || a.status === 'requested'),
      );
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'work-orders', op: 'agenda', error: e, title: 'Agenda', userMessage: 'No se pudieron cargar los turnos.' });
      setAppointmentOptions([]);
    }
  }, [currentBranchId, canLinkAppointment]);

  const handleLinkAppointment = useCallback(
    async (appointmentId: string | null) => {
      setLinkingAppointment(true);
      try {
        await linkWorkOrderToAppointment(workOrderId, appointmentId);
        showSuccess(appointmentId ? 'Turno vinculado' : 'Turno desvinculado');
        setLinkAppointmentOpen(false);
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'agenda', error: e, title: 'Agenda', userMessage: 'No se pudo vincular el turno.' });
      } finally {
        setLinkingAppointment(false);
      }
    },
    [workOrderId, load],
  );

  const handleAddChecklistFromTemplate = useCallback(
    async (template: ChecklistTemplate) => {
      setShowTemplatePicker(false);
      try {
        await addWorkOrderChecklist({
          workOrderId,
          templateName: template.name,
          items: template.items.map((it) => ({ ...it, checked: false, notes: null })),
        });
        showSuccess('Checklist agregado', template.name);
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo agregar el checklist.' });
      }
    },
    [workOrderId, load],
  );

  const handleToggleChecklistItem = useCallback(
    async (checklistId: string, items: WorkOrderChecklistItem[], index: number) => {
      const newItems = items.map((it, i) =>
        i === index ? { ...it, checked: !it.checked } : it,
      );
      try {
        await updateWorkOrderChecklistItems(checklistId, newItems);
        setDetail((d) =>
          d
            ? {
                ...d,
                checklists: d.checklists.map((c) =>
                  c.id === checklistId ? { ...c, items: newItems } : c,
                ),
              }
            : d,
        );
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo actualizar el item.' });
      }
    },
    [],
  );

  const handleConfirmConsumption = useCallback(
    async (partId: string, productName: string, quantity: number) => {
      const ok = await confirm({
        title: 'Confirmar consumo',
        message: `¿Descontar ${quantity}u de "${productName}" del stock?`,
        confirmLabel: 'Confirmar',
      });
      if (!ok) return;
      try {
        await deductWorkOrderPartStock(partId);
        showSuccess('Stock descontado', `${quantity}u de ${productName}`);
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo descontar el stock.' });
      }
    },
    [confirm, load],
  );

  const handleCompleteChecklist = useCallback(
    async (checklistId: string) => {
      try {
        await completeWorkOrderChecklist(checklistId);
        showSuccess('Checklist completado');
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo completar el checklist.' });
      }
    },
    [load],
  );

  const handleRemoveChecklist = useCallback(
    async (checklistId: string) => {
      const ok = await confirm({
        title: 'Eliminar checklist',
        message: '¿Seguro que querés eliminar este checklist?',
        confirmLabel: 'Eliminar',
        destructive: true,
      });
      if (!ok) return;
      try {
        await removeWorkOrderChecklist(checklistId);
        showSuccess('Checklist eliminado');
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo eliminar.' });
      }
    },
    [confirm, load],
  );

  // --- Add task/part modal handlers ---

  const openAddModal = useCallback((mode: 'task' | 'part') => {
    setAddModalMode(mode);
    setProductSearch('');
    setProductResults([]);
    setSelectedProduct(null);
    setItemQuantity('1');
    setItemPrice('');
  }, []);

  const closeAddModal = useCallback(() => {
    setAddModalMode(null);
    setProductSearch('');
    setProductResults([]);
    setSelectedProduct(null);
    setItemQuantity('1');
    setItemPrice('');
  }, []);

  const handleSelectProduct = useCallback((p: ProductSearchRow) => {
    setSelectedProduct(p);
    setProductSearch('');
    setProductResults([]);
    setItemPrice(String(p.price ?? ''));
  }, []);

  const handleSaveItem = useCallback(async () => {
    if (!addModalMode || !workOrderId) return;
    const qty = parseInt(itemQuantity, 10);
    if (!qty || qty < 1) {
      showError('Cantidad inválida', 'La cantidad debe ser mayor a 0.');
      return;
    }
    // If no product selected, use a custom item with the typed name
    const productName = selectedProduct?.name || productSearch.trim();
    if (!productName) {
      showError('Falta el nombre', 'Elegí un producto o escribí un nombre.');
      return;
    }
    const productId = selectedProduct?.id || `custom-${Date.now()}`;
    const unitPrice = parseFloat(itemPrice) || 0;

    setSavingItem(true);
    try {
      if (addModalMode === 'task') {
        await addWorkOrderTask({
          workOrderId,
          productId,
          productName,
          quantity: qty,
          unitPrice,
          assignedToEmployeeId: taskEmployeeId,
          commissionPercent: taskCommission ? parseFloat(taskCommission) : null,
        });
        showSuccess('Tarea agregada', productName);
      } else {
        await addWorkOrderPart({
          workOrderId,
          productId,
          productName,
          quantity: qty,
          unitPrice,
        });
        showSuccess('Repuesto agregado', productName);
      }
      closeAddModal();
      await load();
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo agregar el ítem.' });
    } finally {
      setSavingItem(false);
    }
  }, [addModalMode, workOrderId, selectedProduct, productSearch, itemQuantity, itemPrice, taskEmployeeId, taskCommission, load, closeAddModal]);

  const handleRemoveTask = useCallback(
    async (task: WorkOrderTask) => {
      const ok = await confirm({
        title: 'Eliminar tarea',
        message: `¿Eliminar "${task.productName}" de la OT?`,
        confirmLabel: 'Eliminar',
        destructive: true,
      });
      if (!ok) return;
      try {
        await removeWorkOrderTask(task.id);
        showSuccess('Tarea eliminada');
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo eliminar la tarea.' });
      }
    },
    [confirm, load],
  );

  const handleRemovePart = useCallback(
    async (part: WorkOrderPart) => {
      const ok = await confirm({
        title: 'Eliminar repuesto',
        message: `¿Eliminar "${part.productName}" de la OT?`,
        confirmLabel: 'Eliminar',
        destructive: true,
      });
      if (!ok) return;
      try {
        await removeWorkOrderPart(part.id);
        showSuccess('Repuesto eliminado');
        await load();
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo eliminar el repuesto.' });
      }
    },
    [confirm, load],
  );

  // --- Edit item handlers ---

  const openEditItem = useCallback(
    (type: 'task' | 'part', item: { id: string; productName: string; quantity: number; unitPrice: number; assignedToEmployeeId?: string | null; commissionPercent?: number | null }) => {
      setEditModal({ type, id: item.id, name: item.productName, qty: item.quantity, price: item.unitPrice, employeeId: item.assignedToEmployeeId, commission: item.commissionPercent });
      setEditQty(String(item.quantity));
      setEditPrice(String(item.unitPrice));
      setEditEmployeeId(item.assignedToEmployeeId ?? null);
      setEditCommission(item.commissionPercent != null ? String(item.commissionPercent) : '');
    },
    [],
  );

  const closeEditModal = useCallback(() => {
    setEditModal(null);
    setEditQty('');
    setEditPrice('');
    setEditEmployeeId(null);
    setEditCommission('');
  }, []);

  const handleSaveEditItem = useCallback(async () => {
    if (!editModal) return;
    const qty = parseInt(editQty, 10);
    if (!qty || qty < 1) {
      showError('Cantidad inválida', 'La cantidad debe ser mayor a 0.');
      return;
    }
    const unitPrice = parseFloat(editPrice) || 0;
    setSavingEdit(true);
    try {
      if (editModal.type === 'task') {
        await updateWorkOrderTask({
          taskId: editModal.id,
          quantity: qty,
          unitPrice,
          assignedToEmployeeId: editEmployeeId,
          commissionPercent: editCommission ? parseFloat(editCommission) : null,
        });
        showSuccess('Tarea actualizada', editModal.name);
      } else {
        await updateWorkOrderPart({
          partId: editModal.id,
          quantity: qty,
          unitPrice,
        });
        showSuccess('Repuesto actualizado', editModal.name);
      }
      closeEditModal();
      await load();
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo actualizar el ítem.' });
    } finally {
      setSavingEdit(false);
    }
  }, [editModal, editQty, editPrice, editEmployeeId, editCommission, load, closeEditModal]);

  // --- Edit notes handlers ---

  const openEditNotes = useCallback(() => {
    setNotesDraft(detail?.workOrder.notes ?? '');
    setEditingNotes(true);
  }, [detail]);

  const handleSaveNotes = useCallback(async () => {
    setSavingNotes(true);
    try {
      await updateWorkOrderNotes(workOrderId, notesDraft.trim());
      showSuccess('Notas actualizadas');
      setEditingNotes(false);
      await load();
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudieron actualizar las notas.' });
    } finally {
      setSavingNotes(false);
    }
  }, [workOrderId, notesDraft, load]);

  if (!capabilityGate.allowed) {
    return <ShellContentFrame>{capabilityGate.blocked}</ShellContentFrame>;
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={colors.accent} />
      </View>
    );
  }

  if (error || !detail) {
    return (
      <ShellContentFrame>
        <View style={styles.center}>
          <Text style={styles.errorText}>{error ?? 'No se encontró la orden.'}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => void load()}>
            <Text style={styles.retryText}>Reintentar</Text>
          </TouchableOpacity>
        </View>
      </ShellContentFrame>
    );
  }

  const { workOrder, tasks, parts, photos, checklists } = detail;
  // El cliente ya se muestra en la línea de arriba: para sujeto "cliente" no hay
  // nada extra que decir; para vehículo/mascota/prenda sin resolver, el tipo alcanza.
  const subjectLineText =
    subjectLabel ??
    (workOrder.subjectType !== 'customer' ? SUBJECT_LABELS[workOrder.subjectType] : null);
  const statusColor = WORK_ORDER_STATUS_COLORS[workOrder.status] ?? colors.textSecondary;
  const transitions = nextStatuses(workOrder.status);
  // Separar el destructivo del stepper de progreso (UX audit #1466).
  const progressTransitions = transitions.filter((s) => s !== 'cancelled');
  const canCancelOrder = transitions.includes('cancelled');
  const showUpdatedAt =
    Math.abs(
      new Date(workOrder.updatedAt).getTime() - new Date(workOrder.createdAt).getTime(),
    ) > 60_000;
  const canEditItems = canEdit && !isClosedStatus(workOrder.status);
  const canEditParts = canManageParts && canEditItems;
  const canEditStatus = canChangeStatus && !isClosedStatus(workOrder.status);

  // Totals
  const tasksTotal = tasks.reduce((sum, t) => sum + t.unitPrice * t.quantity, 0);
  const partsTotal = parts.reduce((sum, p) => sum + p.unitPrice * p.quantity, 0);
  const grandTotal = tasksTotal + partsTotal;
  const balanceDue = Math.max(0, grandTotal - (workOrder.amountPaid ?? 0));
  const canCobrarFinal =
    canLinkSale &&
    (workOrder.status === 'ready' || workOrder.status === 'delivered') &&
    workOrder.paymentStatus !== 'paid' &&
    !workOrder.saleId;
  const canCobrarSenia =
    canLinkSale &&
    !isClosedStatus(workOrder.status) &&
    workOrder.paymentStatus !== 'paid' &&
    !workOrder.saleId;

  return (
    <ShellContentFrame>
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.accent]} />
      }
    >
      {/* Header */}
      <View style={styles.headerCard}>
        <View style={styles.headerTop}>
          <Text style={styles.otNumber}>{formatOtLabel(workOrder.otNumber, workOrder.id)}</Text>
          <View style={[styles.badge, { backgroundColor: statusColor }]}>
            <Text style={styles.badgeText}>
              {WORK_ORDER_STATUS_LABELS[workOrder.status] ?? workOrder.status}
            </Text>
          </View>
        </View>
        <Text style={styles.customer}>{workOrder.customerName || 'Sin cliente'}</Text>
        {subjectLineText ? <Text style={styles.subject}>{subjectLineText}</Text> : null}
        {workOrder.saleId ? (
          <TouchableOpacity
            onPress={() => {
              navigateInShell(navigation, 'SaleDetail', { saleId: workOrder.saleId! });
            }}
          >
            <Text style={styles.saleLink}>Venta: {shortId(workOrder.saleId!)}</Text>
          </TouchableOpacity>
        ) : null}
        {workOrder.invoiceId ? (
          <TouchableOpacity
            onPress={() => {
              if (workOrder.saleId) {
                navigateInShell(navigation, 'SaleDetail', { saleId: workOrder.saleId! });
              } else {
                showInfo('Factura', `Factura ${shortId(workOrder.invoiceId!)} asociada`);
              }
            }}
          >
            <Text style={styles.saleLink}>Factura: {shortId(workOrder.invoiceId!)}</Text>
          </TouchableOpacity>
        ) : null}
        {workOrder.notes ? (
          <View style={styles.notesBox}>
            <View style={styles.notesHeader}>
              <Text style={styles.notesLabel}>Notas</Text>
              {canEditItems ? (
                <TouchableOpacity onPress={openEditNotes} testID="work-order-detail.edit-notes">
                  <Text style={styles.editLink}>Editar</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            <Text style={styles.notesText}>{workOrder.notes}</Text>
          </View>
        ) : canEditItems ? (
          <TouchableOpacity onPress={openEditNotes} style={styles.addNotesBtn} testID="work-order-detail.add-notes">
            <Text style={styles.addNotesText}>+ Agregar notas</Text>
          </TouchableOpacity>
        ) : null}
        {canLinkAppointment ? (
          <View style={styles.appointmentBox}>
            {workOrder.appointmentId ? (
              <>
                <Text style={styles.dateText}>
                  Turno vinculado: {shortId(workOrder.appointmentId)}
                </Text>
                <TouchableOpacity
                  onPress={() => void handleLinkAppointment(null)}
                  disabled={linkingAppointment}
                  testID="work-order-detail.unlink-appointment"
                >
                  <Text style={styles.editLink}>Desvincular turno</Text>
                </TouchableOpacity>
              </>
            ) : (
              <TouchableOpacity
                onPress={() => void openLinkAppointment()}
                testID="work-order-detail.link-appointment"
              >
                <Text style={styles.addNotesText}>+ Vincular turno de agenda</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : null}
        <View style={styles.datesRow}>
          <Text style={styles.dateText}>Creada: {formatDateTime(workOrder.createdAt)}</Text>
          {showUpdatedAt ? (
            <Text style={styles.dateText}>Actualizada: {formatDateTime(workOrder.updatedAt)}</Text>
          ) : null}
        </View>
        <TouchableOpacity style={styles.whatsappButton} onPress={() => void handleWhatsApp()} testID="work-order-detail.whatsapp">
          <Text style={styles.whatsappButtonText}>Enviar estado por WhatsApp</Text>
        </TouchableOpacity>
      </View>

      {/* Status transitions */}
      {canEditStatus && transitions.length > 0 ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cambiar estado</Text>
          {progressTransitions.length > 0 ? (
            <View style={styles.actionsRow}>
              {progressTransitions.map((s) => {
                const color = WORK_ORDER_STATUS_COLORS[s] ?? colors.textMuted;
                return (
                  <TouchableOpacity
                    key={s}
                    style={[styles.statusButton, { backgroundColor: color }]}
                    onPress={() => void handleStatusChange(s)}
                    disabled={transitioning}
                    activeOpacity={0.6}
                    testID={`work-order-detail.status-${s}`}
                  >
                    <Text style={styles.statusButtonText}>
                      {WORK_ORDER_STATUS_LABELS[s] ?? s}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : null}
          {canCancelOrder ? (
            <TouchableOpacity
              style={styles.cancelOrderButton}
              onPress={() => void handleStatusChange('cancelled')}
              disabled={transitioning}
              activeOpacity={0.6}
              testID="work-order-detail.status-cancelled"
            >
              <Text style={styles.cancelOrderButtonText}>Cancelar OT</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {/* Cobros / QR */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.addBtn}
          onPress={() => void handleShowQr()}
          disabled={qrLoading}
          testID="work-order-detail.show-qr"
        >
          <Text style={styles.addBtnText}>{qrLoading ? 'Generando QR…' : 'Mostrar QR'}</Text>
        </TouchableOpacity>
      </View>

      {canLinkSale ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cobros</Text>
          <Text style={styles.itemSub}>
            Total {formatMoney(grandTotal)} · Pagado {formatMoney(workOrder.amountPaid ?? 0)} · Saldo{' '}
            {formatMoney(balanceDue)}
          </Text>
          {workOrder.paymentStatus === 'deposit_paid' ? (
            <Text style={styles.itemSub}>Seña registrada</Text>
          ) : null}
          {canCobrarSenia && workOrder.paymentStatus !== 'deposit_paid' ? (
            <TouchableOpacity
              style={[styles.cobrarButton, { marginTop: spacing.sm, backgroundColor: colors.surfaceSecondary }]}
              onPress={() => {
                const suggested = grandTotal > 0 ? Math.round(grandTotal * 0.5 * 100) / 100 : 0;
                setDepositAmountDraft(suggested > 0 ? String(suggested) : '');
                setDepositModalOpen(true);
              }}
              testID="work-order-detail.cobrar-senia"
            >
              <Text style={[styles.cobrarButtonText, { color: colors.textPrimary }]}>Cobrar seña</Text>
            </TouchableOpacity>
          ) : null}
          {canCobrarFinal ? (
            <TouchableOpacity
              style={[styles.cobrarButton, { marginTop: spacing.sm }]}
              onPress={() =>
                handleCobrar(
                  workOrder.amountPaid > 0 ? 'balance' : 'full',
                  workOrder.amountPaid > 0 ? balanceDue : undefined,
                )
              }
              testID="work-order-detail.cobrar"
            >
              <Text style={styles.cobrarButtonText}>
                {workOrder.amountPaid > 0 ? `Cobrar saldo (${formatMoney(balanceDue)})` : 'Cobrar'}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {/* Tasks */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>
            Tareas{tasks.length > 0 ? ` (${tasks.length})` : ''}
          </Text>
          {canEditItems ? (
            <TouchableOpacity
              style={styles.addBtn}
              onPress={() => openAddModal('task')}
              testID="work-order-detail.add-task"
            >
              <Text style={styles.addBtnText}>+ Agregar</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {tasks.length === 0 ? (
          <Text style={styles.emptySection}>Sin tareas</Text>
        ) : (
          tasks.map((t) => (
            <View key={t.id} style={styles.itemCard}>
              <View style={styles.itemCardHeader}>
                <Text style={styles.itemName}>{t.productName || 'Sin nombre'}</Text>
                {canEditItems ? (
                  <View style={styles.itemActions}>
                    <TouchableOpacity
                      onPress={() => openEditItem('task', t)}
                      testID={`work-order-detail.edit-task-${t.id}`}
                    >
                      <Text style={styles.editBtnText}>Editar</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => void handleRemoveTask(t)}
                      testID={`work-order-detail.remove-task-${t.id}`}
                    >
                      <Text style={styles.removeBtnText}>Eliminar</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
              <Text style={styles.itemDetail}>
                Cantidad: {t.quantity}
                {showMoney ? ` · Precio: ${formatMoney(t.unitPrice)}` : ''}
              </Text>
              {showMoney ? (
                <Text style={styles.itemSub}>Subtotal: {formatMoney(t.unitPrice * t.quantity)}</Text>
              ) : null}
              {t.assignedToEmployeeId ? (
                <Text style={styles.itemSub}>
                  Empleado: {employees.find((e) => e.id === t.assignedToEmployeeId)?.name ?? 'Asignado'}
                </Text>
              ) : null}
              {t.commissionPercent != null ? (
                <Text style={styles.itemSub}>Comisión: {t.commissionPercent}%</Text>
              ) : null}
            </View>
          ))
        )}
      </View>

      {/* Parts */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>
            Repuestos{parts.length > 0 ? ` (${parts.length})` : ''}
          </Text>
          {canEditParts ? (
            <TouchableOpacity
              style={styles.addBtn}
              onPress={() => openAddModal('part')}
              testID="work-order-detail.add-part"
            >
              <Text style={styles.addBtnText}>+ Agregar</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {parts.length === 0 ? (
          <Text style={styles.emptySection}>Sin repuestos</Text>
        ) : (
          parts.map((p) => (
            <View key={p.id} style={styles.itemCard}>
              <View style={styles.itemCardHeader}>
                <Text style={styles.itemName}>{p.productName || 'Sin nombre'}</Text>
                {canEditParts ? (
                  <View style={styles.itemActions}>
                    <TouchableOpacity
                      onPress={() => openEditItem('part', p)}
                      testID={`work-order-detail.edit-part-${p.id}`}
                    >
                      <Text style={styles.editBtnText}>Editar</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => void handleRemovePart(p)}
                      testID={`work-order-detail.remove-part-${p.id}`}
                    >
                      <Text style={styles.removeBtnText}>Eliminar</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
              <Text style={styles.itemDetail}>
                Cantidad: {p.quantity}
                {showMoney ? ` · Precio: ${formatMoney(p.unitPrice)}` : ''}
              </Text>
              {showMoney ? (
                <Text style={styles.itemSub}>Subtotal: {formatMoney(p.unitPrice * p.quantity)}</Text>
              ) : null}
              <Text style={styles.itemSub}>
                Stock descontado: {p.stockDeducted ? 'Sí' : 'No'}
              </Text>
              {!p.stockDeducted && canManageParts && !isClosedStatus(workOrder.status) ? (
                <TouchableOpacity
                  style={styles.confirmConsumptionButton}
                  onPress={() => handleConfirmConsumption(p.id, p.productName, p.quantity)}
                  testID={`work-order-detail.confirm-consumption-${p.id}`}
                >
                  <Text style={styles.confirmConsumptionText}>Confirmar consumo</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ))
        )}
      </View>

      {/* Total */}
      {showMoney && (tasks.length > 0 || parts.length > 0) ? (
        <View style={styles.totalCard} testID="work-order-detail.total">
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Tareas</Text>
            <Text style={styles.totalValue}>{formatMoney(tasksTotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Repuestos</Text>
            <Text style={styles.totalValue}>{formatMoney(partsTotal)}</Text>
          </View>
          <View style={styles.totalDivider} />
          <View style={styles.totalRow}>
            <Text style={styles.totalLabelBold}>Total estimado</Text>
            <Text style={styles.totalValueBold}>{formatMoney(grandTotal)}</Text>
          </View>
        </View>
      ) : null}

      {/* Photos */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>
            Fotos{photos.length > 0 ? ` (${photos.length})` : ''}
          </Text>
          {canManagePhotos && canEditItems ? (
            <TouchableOpacity
              style={styles.addBtn}
              onPress={() => void handleAddPhoto()}
              disabled={uploadingPhoto}
              testID="work-order-detail.add-photo"
            >
              <Text style={styles.addBtnText}>{uploadingPhoto ? 'Subiendo…' : '+ Foto'}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {photos.length === 0 ? (
          <Text style={styles.emptySection}>Sin fotos</Text>
        ) : (
          <View style={styles.photosGrid}>
            {photos.map((ph) => (
              <View key={ph.id} style={styles.photoThumb}>
                <TouchableOpacity onPress={() => setPhotoViewerUrl(ph.storagePath)}>
                  <Image
                    source={{ uri: ph.storagePath }}
                    style={styles.photoImage}
                    resizeMode="cover"
                  />
                </TouchableOpacity>
                {ph.caption ? (
                  <Text style={styles.photoCaption} numberOfLines={1}>{ph.caption}</Text>
                ) : null}
                {canManagePhotos && canEditItems ? (
                  <TouchableOpacity
                    onPress={() => void handleRemovePhoto(ph.id)}
                    testID={`work-order-detail.remove-photo-${ph.id}`}
                  >
                    <Text style={styles.removeBtnText}>Eliminar</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Checklists (H-17) */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          Checklists{checklists.length > 0 ? ` (${checklists.length})` : ''}
        </Text>
        {checklists.map((c) => {
          const allChecked = c.items.length > 0 && c.items.every((it) => it.checked);
          return (
            <View key={c.id} style={styles.checklistCard}>
              <View style={styles.checklistHeader}>
                <Text style={styles.checklistName}>{c.templateName || 'Checklist'}</Text>
                {c.completedAt ? (
                  <Text style={styles.checklistDone}>Completado</Text>
                ) : null}
              </View>
              {c.items.map((it, i) => (
                <TouchableOpacity
                  key={i}
                  style={styles.checklistItem}
                  onPress={() => void handleToggleChecklistItem(c.id, c.items, i)}
                  disabled={!!c.completedAt}
                >
                  <View style={[styles.checkbox, it.checked && styles.checkboxChecked]}>
                    {it.checked ? <Text style={styles.checkmark}>✓</Text> : null}
                  </View>
                  <View style={styles.checklistItemText}>
                    <Text style={[styles.checklistLabel, it.checked && styles.checklistLabelDone]}>
                      {it.label}
                      {it.required ? <Text style={styles.requiredAsterisk}> *</Text> : null}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
              {!c.completedAt && canManageChecklists ? (
                <View style={styles.checklistActions}>
                  {allChecked ? (
                    <TouchableOpacity
                      style={styles.completeChecklistBtn}
                      onPress={() => void handleCompleteChecklist(c.id)}
                    >
                      <Text style={styles.completeChecklistBtnText}>Completar</Text>
                    </TouchableOpacity>
                  ) : null}
                  <TouchableOpacity
                    style={styles.removeChecklistBtn}
                    onPress={() => void handleRemoveChecklist(c.id)}
                  >
                    <Text style={styles.removeChecklistBtnText}>Eliminar</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          );
        })}
        {canManageChecklists && canEditItems ? (
          showTemplatePicker ? (
          <View style={styles.templatePicker}>
            {templates.length === 0 ? (
              <Text style={styles.emptySection}>No hay templates disponibles</Text>
            ) : (
              templates.map((t) => (
                <TouchableOpacity
                  key={t.id}
                  style={styles.templateOption}
                  onPress={() => void handleAddChecklistFromTemplate(t)}
                >
                  <Text style={styles.templateOptionText}>{t.name}</Text>
                  <Text style={styles.templateOptionCount}>{t.items.length} ítems</Text>
                </TouchableOpacity>
              ))
            )}
            <TouchableOpacity
              style={styles.cancelTemplateBtn}
              onPress={() => setShowTemplatePicker(false)}
            >
              <Text style={styles.cancelTemplateText}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={styles.addChecklistBtn}
            onPress={() => setShowTemplatePicker(true)}
          >
            <Text style={styles.addChecklistBtnText}>+ Agregar checklist</Text>
          </TouchableOpacity>
        )
        ) : null}
      </View>
      {ConfirmModal}

      <Modal
        visible={linkAppointmentOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setLinkAppointmentOpen(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Vincular turno</Text>
            {appointmentOptions.length === 0 ? (
              <Text style={styles.emptySection}>No hay turnos recientes para vincular.</Text>
            ) : (
              <ScrollView style={{ maxHeight: 320 }}>
                {appointmentOptions.map((a) => (
                  <TouchableOpacity
                    key={a.id}
                    style={styles.templateOption}
                    disabled={linkingAppointment}
                    onPress={() => void handleLinkAppointment(a.id)}
                    testID={`work-order-detail.pick-appointment-${a.id}`}
                  >
                    <Text style={styles.templateOptionText}>{a.customerName}</Text>
                    <Text style={styles.templateOptionCount}>
                      {formatDateTime(a.startsAt)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            <TouchableOpacity
              style={styles.cancelTemplateBtn}
              onPress={() => setLinkAppointmentOpen(false)}
            >
              <Text style={styles.cancelTemplateText}>Cerrar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add task/part modal */}
      <Modal
        visible={addModalMode !== null}
        transparent
        animationType="slide"
        onRequestClose={closeAddModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {addModalMode === 'task' ? 'Agregar tarea' : 'Agregar repuesto'}
            </Text>

            {/* Product search */}
            {selectedProduct ? (
              <View style={styles.selectedProductCard}>
                <View style={styles.selectedProductInfo}>
                  <Text style={styles.selectedProductName}>{selectedProduct.name}</Text>
                  {selectedProduct.sku ? (
                    <Text style={styles.selectedProductSku}>SKU: {selectedProduct.sku}</Text>
                  ) : null}
                </View>
                <TouchableOpacity onPress={() => setSelectedProduct(null)}>
                  <Text style={styles.clearProductText}>Cambiar</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>
                  {addModalMode === 'task' ? 'Buscar servicio' : 'Buscar producto'}
                </Text>
                <TextInput
                  style={styles.modalInput}
                  value={productSearch}
                  onChangeText={setProductSearch}
                  placeholder={addModalMode === 'task' ? 'Nombre del servicio…' : 'Nombre o SKU…'}
                  placeholderTextColor="#9ca3af"
                  autoCorrect={false}
                  testID="work-order-detail.product-search"
                />
                {productSearching ? (
                  <ActivityIndicator size="small" color={colors.accent} style={{ marginTop: 6 }} />
                ) : null}
                {productResults.length > 0 ? (
                  <View style={styles.productResults}>
                    {productResults.slice(0, 10).map((p) => (
                      <TouchableOpacity
                        key={p.id}
                        style={styles.productOption}
                        onPress={() => handleSelectProduct(p)}
                      >
                        <Text style={styles.productOptionName}>{p.name}</Text>
                        <Text style={styles.productOptionPrice}>{formatMoney(p.price)}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : null}
                {productSearch.trim().length >= 2 && !productSearching && productResults.length === 0 ? (
                  <Text style={styles.noResultsModal}>
                    No se encontraron productos. Podés usar el texto escrito como nombre del ítem.
                  </Text>
                ) : null}
              </View>
            )}

            {/* Quantity + price */}
            <View style={styles.modalRow}>
              <View style={styles.modalFieldFlex}>
                <Text style={styles.modalLabel}>Cantidad</Text>
                <TextInput
                  style={styles.modalInput}
                  value={itemQuantity}
                  onChangeText={setItemQuantity}
                  keyboardType="numeric"
                  placeholder="1"
                  placeholderTextColor="#9ca3af"
                  testID="work-order-detail.item-quantity"
                />
              </View>
              <View style={styles.modalFieldFlex}>
                <Text style={styles.modalLabel}>Precio unit.</Text>
                <TextInput
                  style={styles.modalInput}
                  value={itemPrice}
                  onChangeText={setItemPrice}
                  keyboardType="numeric"
                  placeholder="0"
                  placeholderTextColor="#9ca3af"
                  testID="work-order-detail.item-price"
                />
              </View>
            </View>

            {/* Employee + commission (tasks only) */}
            {addModalMode === 'task' && canAssignTasks ? (
              <View style={styles.modalRow}>
                <View style={styles.modalFieldFlex}>
                  <Text style={styles.modalLabel}>Empleado</Text>
                  <View style={styles.pickerBorder}>
                    {employees.length === 0 ? (
                      <Text style={styles.pickerEmpty}>Sin empleados</Text>
                    ) : (
                      employees.map((emp) => (
                        <TouchableOpacity
                          key={emp.id}
                          style={[
                            styles.pickerItem,
                            taskEmployeeId === emp.id && styles.pickerItemActive,
                          ]}
                          onPress={() =>
                            setTaskEmployeeId(taskEmployeeId === emp.id ? null : emp.id)
                          }
                        >
                          <Text
                            style={[
                              styles.pickerItemText,
                              taskEmployeeId === emp.id && styles.pickerItemTextActive,
                            ]}
                          >
                            {emp.name}
                          </Text>
                        </TouchableOpacity>
                      ))
                    )}
                  </View>
                </View>
                <View style={{ flex: 0.4 }}>
                  <Text style={styles.modalLabel}>Comisión %</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={taskCommission}
                    onChangeText={setTaskCommission}
                    keyboardType="numeric"
                    placeholder="0"
                    placeholderTextColor="#9ca3af"
                    testID="work-order-detail.task-commission"
                  />
                </View>
              </View>
            ) : null}

            {/* Modal actions */}
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={closeAddModal} disabled={savingItem}>
                <Text style={styles.modalCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalSaveBtn, savingItem && styles.modalSaveBtnDisabled]}
                onPress={() => void handleSaveItem()}
                disabled={savingItem}
                testID="work-order-detail.save-item"
              >
                {savingItem ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={styles.modalSaveText}>Agregar</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit item modal */}
      <Modal
        visible={editModal !== null}
        transparent
        animationType="fade"
        onRequestClose={closeEditModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {editModal?.type === 'task' ? 'Editar tarea' : 'Editar repuesto'}
            </Text>
            <Text style={styles.modalSubtitle}>{editModal?.name}</Text>

            <View style={styles.modalRow}>
              <View style={styles.modalFieldFlex}>
                <Text style={styles.modalLabel}>Cantidad</Text>
                <TextInput
                  style={styles.modalInput}
                  value={editQty}
                  onChangeText={setEditQty}
                  keyboardType="numeric"
                  placeholder="1"
                  placeholderTextColor="#9ca3af"
                  testID="work-order-detail.edit-quantity"
                />
              </View>
              <View style={styles.modalFieldFlex}>
                <Text style={styles.modalLabel}>Precio unit.</Text>
                <TextInput
                  style={styles.modalInput}
                  value={editPrice}
                  onChangeText={setEditPrice}
                  keyboardType="numeric"
                  placeholder="0"
                  placeholderTextColor="#9ca3af"
                  testID="work-order-detail.edit-price"
                />
              </View>
            </View>

            {editModal?.type === 'task' && canAssignTasks ? (
              <View style={styles.modalRow}>
                <View style={styles.modalFieldFlex}>
                  <Text style={styles.modalLabel}>Empleado</Text>
                  <View style={styles.pickerBorder}>
                    {employees.length === 0 ? (
                      <Text style={styles.pickerEmpty}>Sin empleados</Text>
                    ) : (
                      employees.map((emp) => (
                        <TouchableOpacity
                          key={emp.id}
                          style={[
                            styles.pickerItem,
                            editEmployeeId === emp.id && styles.pickerItemActive,
                          ]}
                          onPress={() =>
                            setEditEmployeeId(editEmployeeId === emp.id ? null : emp.id)
                          }
                        >
                          <Text
                            style={[
                              styles.pickerItemText,
                              editEmployeeId === emp.id && styles.pickerItemTextActive,
                            ]}
                          >
                            {emp.name}
                          </Text>
                        </TouchableOpacity>
                      ))
                    )}
                  </View>
                </View>
                <View style={{ flex: 0.4 }}>
                  <Text style={styles.modalLabel}>Comisión %</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={editCommission}
                    onChangeText={setEditCommission}
                    keyboardType="numeric"
                    placeholder="0"
                    placeholderTextColor="#9ca3af"
                    testID="work-order-detail.edit-commission"
                  />
                </View>
              </View>
            ) : null}

            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={closeEditModal} disabled={savingEdit}>
                <Text style={styles.modalCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalSaveBtn, savingEdit && styles.modalSaveBtnDisabled]}
                onPress={() => void handleSaveEditItem()}
                disabled={savingEdit}
                testID="work-order-detail.save-edit"
              >
                {savingEdit ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={styles.modalSaveText}>Guardar</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit notes modal */}
      <Modal
        visible={editingNotes}
        transparent
        animationType="slide"
        onRequestClose={() => setEditingNotes(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Editar notas</Text>
            <TextInput
              style={[styles.modalInput, styles.notesModalInput]}
              value={notesDraft}
              onChangeText={setNotesDraft}
              placeholder="Notas internas (opcional)"
              placeholderTextColor="#9ca3af"
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              testID="work-order-detail.notes-input"
            />
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalCancelBtn}
                onPress={() => setEditingNotes(false)}
                disabled={savingNotes}
              >
                <Text style={styles.modalCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalSaveBtn, savingNotes && styles.modalSaveBtnDisabled]}
                onPress={() => void handleSaveNotes()}
                disabled={savingNotes}
                testID="work-order-detail.save-notes"
              >
                {savingNotes ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={styles.modalSaveText}>Guardar</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Photo viewer modal */}
      <Modal
        visible={photoViewerUrl !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setPhotoViewerUrl(null)}
      >
        {photoViewerUrl ? (
          <View style={styles.photoViewerOverlay}>
            <TouchableOpacity
              style={styles.photoViewerClose}
              onPress={() => setPhotoViewerUrl(null)}
            >
              <Text style={styles.photoViewerCloseText}>Cerrar</Text>
            </TouchableOpacity>
            <Image
              source={{ uri: photoViewerUrl }}
              style={styles.photoViewerImage}
              resizeMode="contain"
            />
          </View>
        ) : null}
      </Modal>

      {/* Seña amount modal */}
      <Modal
        visible={depositModalOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setDepositModalOpen(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Cobrar seña</Text>
            <Text style={styles.modalLabel}>Monto</Text>
            <TextInput
              style={styles.modalInput}
              value={depositAmountDraft}
              onChangeText={setDepositAmountDraft}
              keyboardType="numeric"
              placeholder="Ej. 10000"
              placeholderTextColor="#9ca3af"
              testID="work-order-detail.deposit-amount"
            />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setDepositModalOpen(false)}>
                <Text style={styles.modalCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalSaveBtn}
                onPress={() => {
                  const n = Number(String(depositAmountDraft).replace(',', '.'));
                  handleCobrar('deposit', n);
                }}
                testID="work-order-detail.deposit-confirm"
              >
                <Text style={styles.modalSaveText}>Ir a POS</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* QR modal */}
      <Modal
        visible={qrToken != null}
        transparent
        animationType="fade"
        onRequestClose={() => setQrToken(null)}
      >
        {qrToken ? (
          <View style={styles.modalOverlay}>
            <View style={[styles.modalContent, { alignItems: 'center' }]}>
              <Text style={styles.modalTitle}>QR de la OT</Text>
              <Text style={styles.itemSub}>Escaneá para ver el estado (sin precios)</Text>
              <View style={{ marginVertical: 16 }}>
                <QRCode
                  value={`${customerPortalBaseUrl()}/ot/${qrToken}`}
                  size={200}
                />
              </View>
              <Text style={[styles.itemSub, { textAlign: 'center' }]} selectable>
                {`${customerPortalBaseUrl()}/ot/${qrToken}`}
              </Text>
              <TouchableOpacity
                style={[styles.modalSaveBtn, { marginTop: 16 }]}
                onPress={() => setQrToken(null)}
                testID="work-order-detail.qr-close"
              >
                <Text style={styles.modalSaveText}>Cerrar</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
      </Modal>
    </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    padding: 16,
    paddingBottom: 32,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  headerCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: 16,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  otNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
  },
  badgeText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '600',
  },
  customer: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.textSecondary,
    marginTop: 8,
  },
  subject: {
    fontSize: 14,
    color: colors.textMuted,
    marginTop: 2,
  },
  notesBox: {
    marginTop: 12,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    padding: 10,
  },
  notesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  notesLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textMuted,
  },
  editLink: {
    fontSize: 12,
    color: colors.accent,
    fontWeight: '600',
  },
  notesText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  addNotesBtn: {
    marginTop: 12,
    paddingVertical: 8,
    alignItems: 'flex-start',
  },
  addNotesText: {
    color: colors.accent,
    fontSize: 14,
    fontWeight: '600',
  },
  appointmentBox: {
    marginTop: 10,
    gap: 4,
  },
  datesRow: {
    marginTop: 12,
    gap: 2,
  },
  dateText: {
    fontSize: 12,
    color: colors.textMuted,
  },
  section: {
    marginTop: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  addBtn: {
    backgroundColor: colors.accentSoft,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  addBtnText: {
    color: colors.accent,
    fontSize: 13,
    fontWeight: '600',
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  statusButton: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 8,
  },
  statusButtonText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '600',
  },
  // Cancelación va separada del stepper de progreso: outlined + danger, no chip lleno.
  cancelOrderButton: {
    marginTop: spacing.sm,
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.danger,
  },
  cancelOrderButtonText: {
    color: colors.danger,
    fontSize: 14,
    fontWeight: '600',
  },
  cobrarButton: {
    backgroundColor: '#16a34a',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  cobrarButtonText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '700',
  },
  itemCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
  },
  itemCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  itemName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.textPrimary,
    flex: 1,
  },
  removeBtnText: {
    color: colors.danger,
    fontSize: 12,
    fontWeight: '600',
  },
  editBtnText: {
    color: colors.accent,
    fontSize: 12,
    fontWeight: '600',
    marginRight: 12,
  },
  itemActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pickerBorder: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 6,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  pickerItem: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
  },
  pickerItemActive: {
    backgroundColor: colors.accent,
  },
  pickerItemText: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  pickerItemTextActive: {
    color: colors.surface,
    fontWeight: '600',
  },
  pickerEmpty: {
    fontSize: 12,
    color: colors.textMuted,
    paddingVertical: 4,
  },
  itemDetail: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: 4,
  },
  itemSub: {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 2,
  },
  confirmConsumptionButton: {
    backgroundColor: '#0891b2',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  confirmConsumptionText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '600',
  },
  saleLink: {
    fontSize: 12,
    color: colors.accent,
    marginTop: 4,
    fontWeight: '500',
  },
  emptySection: {
    fontSize: 14,
    color: colors.textMuted,
  },
  errorText: {
    fontSize: 15,
    color: colors.danger,
    textAlign: 'center',
    marginBottom: 12,
  },
  retryButton: {
    backgroundColor: colors.accent,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  retryText: {
    color: colors.surface,
    fontWeight: '600',
  },
  whatsappButton: {
    backgroundColor: '#25D366',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 10,
  },
  whatsappButtonText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '600',
  },
  // Total card
  totalCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: 14,
    marginTop: 16,
    borderWidth: 1,
    borderColor: colors.controlSurface,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  totalLabel: {
    fontSize: 14,
    color: colors.textMuted,
  },
  totalValue: {
    fontSize: 14,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  totalDivider: {
    height: 1,
    backgroundColor: colors.controlSurface,
    marginVertical: 6,
  },
  totalLabelBold: {
    fontSize: 16,
    color: colors.textPrimary,
    fontWeight: '700',
  },
  totalValueBold: {
    fontSize: 16,
    color: '#16a34a',
    fontWeight: '700',
  },
  // Photos
  photosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  photoThumb: {
    width: 100,
    height: 100,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f3f4f6',
  },
  photoImage: {
    width: '100%',
    height: '100%',
  },
  photoCaption: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: colors.surface,
    fontSize: 10,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  photoViewerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoViewerClose: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 1,
  },
  photoViewerCloseText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '600',
  },
  photoViewerImage: {
    width: '90%',
    height: '70%',
  },
  // Checklist styles (H-17)
  checklistCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.controlSurface,
  },
  checklistHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  checklistName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  checklistDone: {
    fontSize: 12,
    color: '#16a34a',
    fontWeight: '600',
  },
  checklistItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: colors.border,
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#16a34a',
    borderColor: '#16a34a',
  },
  checkmark: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '700',
  },
  checklistItemText: {
    flex: 1,
  },
  checklistLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  checklistLabelDone: {
    textDecorationLine: 'line-through',
    color: colors.textMuted,
  },
  requiredAsterisk: {
    color: colors.danger,
    fontWeight: '700',
  },
  checklistActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  completeChecklistBtn: {
    backgroundColor: '#16a34a',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  completeChecklistBtnText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '600',
  },
  removeChecklistBtn: {
    backgroundColor: '#fee2e2',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  removeChecklistBtnText: {
    color: colors.danger,
    fontSize: 13,
    fontWeight: '600',
  },
  addChecklistBtn: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 4,
  },
  addChecklistBtnText: {
    color: colors.accent,
    fontSize: 14,
    fontWeight: '600',
  },
  templatePicker: {
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: 8,
    marginTop: 4,
  },
  templateOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 8,
    backgroundColor: colors.surface,
    borderRadius: 6,
    marginBottom: 4,
  },
  templateOptionText: {
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: '500',
  },
  templateOptionCount: {
    fontSize: 12,
    color: colors.textMuted,
  },
  cancelTemplateBtn: {
    paddingVertical: 8,
    alignItems: 'center',
  },
  cancelTemplateText: {
    color: colors.textMuted,
    fontSize: 13,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: 20,
    width: '100%',
    maxWidth: 500,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 14,
    color: colors.textMuted,
    marginBottom: 16,
  },
  modalField: {
    marginBottom: 14,
  },
  modalFieldFlex: {
    flex: 1,
    marginBottom: 14,
    marginHorizontal: 4,
  },
  modalLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: 4,
  },
  modalInput: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: colors.textPrimary,
  },
  notesModalInput: {
    minHeight: 120,
  },
  modalRow: {
    flexDirection: 'row',
    gap: 8,
  },
  selectedProductCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.accentSoft,
    borderWidth: 1,
    borderColor: '#bfdbfe',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 14,
  },
  selectedProductInfo: {
    flex: 1,
  },
  selectedProductName: {
    fontSize: 15,
    color: colors.accentStrong,
    fontWeight: '600',
  },
  selectedProductSku: {
    fontSize: 12,
    color: colors.accent,
    marginTop: 2,
  },
  clearProductText: {
    color: colors.textMuted,
    fontSize: 13,
    fontWeight: '600',
  },
  productResults: {
    marginTop: 4,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    overflow: 'hidden',
    maxHeight: 250,
  },
  productOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  productOptionName: {
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: '500',
    flex: 1,
  },
  productOptionPrice: {
    fontSize: 13,
    color: colors.textMuted,
  },
  noResultsModal: {
    fontSize: 13,
    color: colors.textMuted,
    marginTop: 6,
    fontStyle: 'italic',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: colors.controlSurface,
  },
  modalCancelText: {
    color: colors.textSecondary,
    fontSize: 16,
    fontWeight: '600',
  },
  modalSaveBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: colors.accent,
  },
  modalSaveBtnDisabled: {
    opacity: 0.6,
  },
  modalSaveText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '700',
  },
});
